import _ from 'es-toolkit/compat';

import type { Nullable } from '@clearverify/types';

import {
  EmployeeRole,
  EmployerStaffRole,
  PeoPayrollProviderStaffRole,
  PeoStaffRole,
  PlatformStaffRole,
  VerifierStaffRole,
} from 'src/api';
import { BusinessRole, UserPermissions } from 'src/constants/user';

type Roleable = Nullable<{ role?: Nullable<string> }>;
type WithId = Nullable<{ id?: Nullable<string> }>;

type UserWithRoles = Nullable<{
  employerStaff?: Nullable<{ role?: Nullable<EmployerStaffRole> }>;
  verifierStaff?: Nullable<{ role?: Nullable<VerifierStaffRole> }>;
  platformStaff?: Nullable<{ role?: Nullable<PlatformStaffRole> }>;
  employee?: Nullable<{ role?: Nullable<EmployeeRole> }>;
  peoStaff?: Nullable<{ role?: Nullable<PeoStaffRole> }>;
  peoPayrollProviderStaff?: Nullable<{ role?: Nullable<PeoPayrollProviderStaffRole> }>;
}>;

export const getBusinessRoles = (user: UserWithRoles) => {
  const roles: string[] = [];

  if (user?.employerStaff?.role) roles.push(user.employerStaff.role);
  if (user?.verifierStaff?.role) roles.push(user.verifierStaff.role);
  if (user?.platformStaff?.role) roles.push(user.platformStaff.role);
  if (user?.employee?.role) roles.push(user.employee.role);
  if (user?.peoStaff?.role) roles.push(user.peoStaff.role);
  if (user?.peoPayrollProviderStaff?.role) roles.push(user.peoPayrollProviderStaff.role);

  return roles as BusinessRole[];
};

export const hasUserOneOfRoles = (user: UserWithRoles, roleNames: BusinessRole[]) => {
  return getBusinessRoles(user).some(role => roleNames.includes(role));
};

export const isKnownBusinessRole = (roleName: Nullable<string>): roleName is BusinessRole => {
  return !_.isNil(roleName) && Object.values(BusinessRole as object).includes(roleName);
};

export const hasOneOfRoles = <T extends Roleable>(user: T, roleNames: BusinessRole[]) => {
  if (!user?.role) return false;
  return roleNames.includes(user.role as BusinessRole);
};

export const getBusinessRolesId = <
  TUser extends Nullable<
    Partial<
      Nullable<{
        employerStaff?: Nullable<{ employerId?: string }>;
        verifierStaff?: Nullable<{ verifierId?: string }>;
        employee?: Nullable<WithId>;
      }>
    >
  >,
>(
  user: TUser,
  userPermissions: UserPermissions,
) => {
  if (userPermissions.hasVerifierRoles) {
    return user?.verifierStaff?.verifierId;
  }
  if (userPermissions.hasEmployerRoles) {
    return user?.employerStaff?.employerId;
  }
  if (userPermissions.isEmployee) {
    return user?.employee?.id;
  }

  return null;
};
